import { Category, Conta } from "@/types/database";
import { ParsedFinancialCommand } from "@/lib/assistant-parser";
import { subDays, format, getMonth, getYear, setDate, setMonth } from "date-fns";

export type AssistantExecutionMode = "rule-only" | "local-model-candidate";
export type AssistantParseSource = "rule-parser" | "local-model";

export interface AssistantCapabilities {
  hasWebGPU: boolean;
  hasWebAssembly: boolean;
  isMobile: boolean;
}

export interface AssistantTransactionDraft {
  tipo: "entrada" | "saida";
  valor: number;
  descricao: string;
  categoriaId: string;
  contaId: string;
  data: string;
  source: AssistantParseSource;
}

export interface AssistantInterpretResult {
  parsed: ParsedFinancialCommand;
  source: AssistantParseSource;
}

interface ResolveCategoryParams {
  categories: Category[];
  categoryHint: string | null;
  tipo: "entrada" | "saida";
  description?: string;
}

interface BuildDraftParams {
  parsed: ParsedFinancialCommand;
  categories: Category[];
  accounts: Conta[];
  nowDate: string;
  source?: AssistantParseSource;
}

interface InterpretCommandParams {
  input: string;
  mode: AssistantExecutionMode;
  parseRule: (text: string) => ParsedFinancialCommand | null;
  parseLocalModel?: (text: string) => Promise<ParsedFinancialCommand | null>;
}

const hintCategoryMap: Record<string, string[]> = {
  transporte: ["transporte", "uber", "taxi", "combustível", "combustivel"],
  alimentacao: ["alimentação", "alimentacao", "mercado", "supermercado", "restaurante"],
  moradia: ["moradia", "casa", "aluguel", "contas"],
  saude: ["saúde", "saude", "farmácia", "farmacia", "médico", "medico"],
  lazer: ["lazer", "entretenimento", "diversão", "diversao"],
  salario: ["salário", "salario", "renda", "receita"],
  renda_extra: ["renda extra", "extra", "freela", "freelance"],
};

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

function isRuleParseHighConfidence(parsed: ParsedFinancialCommand): boolean {
  if (!parsed.categoryHint) return false;
  if (parsed.amount <= 0) return false;
  if (!parsed.description.trim()) return false;
  return true;
}

function resolveHintCandidates(categoryHint: string | null) {
  if (!categoryHint) return [];
  return hintCategoryMap[categoryHint] || [categoryHint];
}

export function resolveCategoryIdByHint({
  categories,
  categoryHint,
  tipo,
  description,
}: ResolveCategoryParams): string {
  const filteredCategories = categories.filter((category) => category.tipo === "ambos" || category.tipo === tipo);
  const normalizedHint = normalize(categoryHint || "");

  if (normalizedHint) {
    const selectedByDirectHint = filteredCategories.find((category) => {
      const categoryName = normalize(category.nome);
      return categoryName === normalizedHint || categoryName.includes(normalizedHint);
    });

    if (selectedByDirectHint) {
      return selectedByDirectHint.id;
    }
  }

  const candidates = resolveHintCandidates(categoryHint);

  const selectedByHint = filteredCategories
    .find((category) => {
      const categoryName = normalize(category.nome);
      return candidates.some((candidate) => categoryName.includes(normalize(candidate)));
    });

  if (selectedByHint) {
    return selectedByHint.id;
  }

  const normalizedDescription = normalize(description || "");
  if (!normalizedDescription) {
    return "none";
  }

  const selectedByDescription = filteredCategories.find((category) => {
      const categoryName = normalize(category.nome);
      return normalizedDescription.includes(categoryName) || categoryName.includes(normalizedDescription);
    });

  return selectedByDescription?.id || "none";
}

const resolveDate = (dateHint: string | null | undefined, defaultDate: string): string => {
  if (!dateHint) return defaultDate;

  const today = new Date();
  
  if (dateHint === "hoje") return format(today, "yyyy-MM-dd");
  if (dateHint === "ontem") return format(subDays(today, 1), "yyyy-MM-dd");
  if (dateHint === "anteontem") return format(subDays(today, 2), "yyyy-MM-dd");
  if (dateHint === "amanha" || dateHint === "amanhã") return format(subDays(today, -1), "yyyy-MM-dd");

  // Format "dia X do Y" or "dia X de mes"
  const voiceDateMatch = dateHint.match(/\bdia\s+(\d{1,2})\s+(?:do|de)\s+(?:mes\s+)?(\d{1,2}|janeiro|fevereiro|marco|março|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro)\b/);
  if (voiceDateMatch) {
    const day = parseInt(voiceDateMatch[1], 10);
    let month = 0; // zero-based em JS

    const monthStr = voiceDateMatch[2].toLowerCase();
    const monthsMap: Record<string, number> = {
      janeiro: 0, fevereiro: 1, marco: 2, março: 2, abril: 3, maio: 4, junho: 5,
      julho: 6, agosto: 7, setembro: 8, outubro: 9, novembro: 10, dezembro: 11
    };

    if (monthsMap[monthStr] !== undefined) {
      month = monthsMap[monthStr];
    } else {
      month = parseInt(monthStr, 10) - 1;
    }

    // Se o mês já passou e é fim de ano, talvez seja do ano passado, mas por segurança assumimos ano atual
    const targetDate = setMonth(setDate(today, day), month);
    return format(targetDate, "yyyy-MM-dd");
  }

  // Format "dia X"
  const simpleVoiceDateMatch = dateHint.match(/\bdia\s+(\d{1,2})\b/);
  if (simpleVoiceDateMatch) {
    const day = parseInt(simpleVoiceDateMatch[1], 10);
    const targetDate = setDate(today, day);
    return format(targetDate, "yyyy-MM-dd");
  }

  // Format numérico "DD/MM" ou "DD/MM/YYYY"
  const numericDateMatch = dateHint.match(/\b(\d{1,2})[./](\d{1,2})(?:[./](\d{2,4}))?\b/);
  if (numericDateMatch) {
    const day = parseInt(numericDateMatch[1], 10);
    const month = parseInt(numericDateMatch[2], 10) - 1;
    const year = numericDateMatch[3] 
      ? (numericDateMatch[3].length === 2 ? 2000 + parseInt(numericDateMatch[3], 10) : parseInt(numericDateMatch[3], 10))
      : getYear(today);

    const targetDate = new Date(year, month, day);
    return format(targetDate, "yyyy-MM-dd");
  }

  return defaultDate;
};

const resolveAccount = (accountHint: string | null | undefined, accounts: Conta[]): string => {
  if (!accountHint || accounts.length === 0) {
    return accounts[0]?.id || "";
  }

  const hintNormalized = normalize(accountHint);
  
  // Tenta achar correspondência exata primeiro
  const exactMatch = accounts.find((a) => normalize(a.nome) === hintNormalized);
  if (exactMatch) return exactMatch.id;

  // Tenta correspondência parcial
  const partialMatch = accounts.find((a) => normalize(a.nome).includes(hintNormalized) || hintNormalized.includes(normalize(a.nome)));
  if (partialMatch) return partialMatch.id;

  return accounts[0]?.id || "";
};

export function buildAssistantTransactionDraft({
  parsed,
  categories,
  accounts,
  nowDate,
  source = "rule-parser",
}: BuildDraftParams): AssistantTransactionDraft {
  const tipo: "entrada" | "saida" = parsed.type === "income" ? "entrada" : "saida";
  const categoriaId = resolveCategoryIdByHint({
    categories,
    categoryHint: parsed.categoryHint,
    tipo,
    description: parsed.description,
  });

  return {
    tipo,
    valor: parsed.amount,
    descricao: parsed.description,
    categoriaId,
    contaId: resolveAccount(parsed.accountHint, accounts),
    data: resolveDate(parsed.dateHint, nowDate),
    source,
  };
}

export async function interpretCommandWithFallback({
  input,
  mode,
  parseRule,
  parseLocalModel,
}: InterpretCommandParams): Promise<AssistantInterpretResult | null> {
  const ruleParsed = parseRule(input);

  if (ruleParsed && (mode === "rule-only" || isRuleParseHighConfidence(ruleParsed) || !parseLocalModel)) {
    return {
      parsed: ruleParsed,
      source: "rule-parser",
    };
  }

  if (mode === "local-model-candidate" && parseLocalModel) {
    try {
      const localParsed = await parseLocalModel(input);
      if (localParsed) {
        return {
          parsed: localParsed,
          source: "local-model",
        };
      }
    } catch {
      if (!ruleParsed) return null;
      return {
        parsed: ruleParsed,
        source: "rule-parser",
      };
    }
  }
  if (!ruleParsed) return null;
  return {
    parsed: ruleParsed,
    source: "rule-parser",
  };
}

export function inferAssistantExecutionMode(capabilities: AssistantCapabilities): AssistantExecutionMode {
  if (!capabilities.hasWebGPU && !capabilities.hasWebAssembly) {
    return "rule-only";
  }
  return "local-model-candidate";
}

export function detectAssistantCapabilities(win: Window = window, nav: Navigator = navigator): AssistantCapabilities {
  const hasWebGPU = "gpu" in nav;
  const hasWebAssembly = typeof WebAssembly !== "undefined";
  const isMobile = /android|iphone|ipad|ipod/i.test(win.navigator.userAgent || "");

  return {
    hasWebGPU,
    hasWebAssembly,
    isMobile,
  };
}
